import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import AutoExpandingTextArea from "./Components/AutoExpandingTextArea.tsx";
import PlayerChip from "./Components/PlayerChip.tsx";
import settingsIcon from "./photos/settings.svg";

interface PublicPlayerData {
  sessionId: string;
  name: string;
  charCount: number;
  hasSubmittedInstructions: boolean;
  customPromptCount: number;
}

interface AIRoomLobbyProps {
  roomName: string;
  sessionId: string;
  socket: any;
  players: PublicPlayerData[];
  setAIRoomState: (state: string) => void;
  setRoomState: (state: string) => void;
  waitingForPrompts: boolean;
  showInfoMessage: (
    type: string,
    message: string,
    isAdd: boolean,
    duration?: string,
    isDelay?: boolean,
  ) => void;
  myVote: {
    craziness: number;
    sexualScale: number;
    drinkAmount: number;
  } | null;
  setupDone: boolean;
  isAdmin: boolean;
}

function AIRoomLobby({
  roomName,
  sessionId,
  socket,
  players,
  setAIRoomState,
  setRoomState,
  waitingForPrompts,
  showInfoMessage,
  myVote,
  setupDone,
  isAdmin,
}: AIRoomLobbyProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<"content" | "player">("content");
  const [dots, setDots] = useState(".");
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!waitingForPrompts) {
      setDots(".");
      return;
    }
    const id = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "." : d + "."));
    }, 400);
    return () => clearInterval(id);
  }, [waitingForPrompts]);

  const handleAdd = () => {
    const value = input.trim();
    if (!value) return;
    if (mode === "content") {
      socket.emit("ai room add prompt", { roomName, sessionId, prompt: value });
    } else {
      socket.emit("ai room add label", { roomName, name: value });
    }
    setInput("");
    showInfoMessage("", t("added"), true, "short");
  };

  const toggleMode = () => {
    setMode(mode === "content" ? "player" : "content");
    setInput("");
  };

  const hasVoted = myVote !== null;
  const isInitialGenerating = waitingForPrompts && !setupDone;

  const getMainButtonLabel = () => {
    if (setupDone) return "FORTSETT SPILLET";
    if (isInitialGenerating) return `GENERER${dots}`;
    return "GENERER ROM";
  };

  const handleMainButton = () => {
    if (setupDone) {
      setAIRoomState("done");
      return;
    }
    if (isInitialGenerating) return;
    if (!hasVoted) {
      setAIRoomState("vote");
      return;
    }
    socket.emit("ai room generate", { roomName, sessionId });
  };

  return (
    <div className="App-header">
      {/* Top bar with room name as clickable settings link */}
      <div className="container-row lift-topbar">
        <div className="container-row-start">
          <div
            className="button-tertiary-m button-text-m text-color-black"
            onClick={() => setRoomState("start screen")}
            style={{ maxHeight: "32.5px" }}
          >
            {t("back")}
          </div>
        </div>
        <div
          className="container-row"
          onClick={() => setAIRoomState("vote")}
          style={{ alignItems: "center", cursor: "pointer" }}
        >
          <div
            className="text-h3 text-color-black left-margin-s text-force-break button-tertiary-m text-color-black"
            style={{ maxHeight: "32.5px" }}
          >
            {roomName}
          </div>
          <img
            src={settingsIcon}
            alt="Settings"
            className="settings-icon left-margin-m"
            style={{ marginBottom: "-11px" }}
          />
        </div>
      </div>

      {/* Main action button — top, prominent */}
      <div
        className="button-primary black-fill button-text-l text-color-pink shadow-pink box-button-center"
        onClick={handleMainButton}
        style={{
          zIndex: 1,
          opacity: isInitialGenerating ? 0.5 : 1,
          pointerEvents: isInitialGenerating ? "none" : "auto",
        }}
      >
        {!hasVoted && !setupDone ? "INNSTILLINGER" : getMainButtonLabel()}
      </div>

      {/* Player count background */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          fontSize: "200px",
          fontWeight: "bold",
          opacity: 0.05,
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        {players.length}
      </div>

      {/* Player chips — takes all available space */}
      <div
        style={{
          zIndex: 1,
          width: "100%",
          flex: 1,
          marginTop: 16,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <div className="names-container">
          {players.map((player) => {
            const isMe = player.sessionId === sessionId;
            return (
              <PlayerChip
                key={player.sessionId}
                name={player.name}
                charCount={player.charCount}
                hasSubmittedInstructions={player.hasSubmittedInstructions}
                isMe={isMe}
                canKick={isAdmin && !isMe}
                onClick={
                  isMe
                    ? () => setAIRoomState("edit-profile")
                    : isAdmin
                      ? () =>
                          socket.emit("ai room kick", {
                            roomName,
                            sessionId,
                            targetSessionId: player.sessionId,
                          })
                      : undefined
                }
              />
            );
          })}
        </div>
      </div>

      {/* Bottom: toggle + input + add button */}
      <div style={{ zIndex: 1, width: "100%" }}>
        {isAdmin && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 16,
            }}
          >
            <div
              className="button-tertiary-s button-text-s text-color-black"
              onClick={toggleMode}
              style={{ maxHeight: "25px", lineHeight: 1.2, cursor: "pointer" }}
            >
              {mode === "content" ? "LEGG TIL SPILLER" : "LEGG TIL INNHOLD"}
            </div>
          </div>
        )}
        <div className="container">
          <AutoExpandingTextArea
            value={input}
            onChange={setInput}
            textareaRef={textAreaRef}
            placeholder={
              mode === "content" ? "SKRIV UTFORDRING ..." : "SKRIV NAVN ..."
            }
            maxLength={mode === "content" ? 280 : 20}
          />
          <div
            className="button-primary button-text-l shadow-black text-color-black box-button-center"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleAdd}
          >
            {t("add")}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIRoomLobby;
